import { AlertContainer } from "@nova/component-alert-container"
import { ButtonSecondary, ButtonTertiary, ButtonPrimary } from "@nova/component-btn"
import { Dropdown, DropdownPosition } from "@nova/component-dropdown"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { InputOutlined } from "@nova/ds-component-input"
import { useAppDispatch } from "integrations/redux/hooks"
import { useState, useMemo, useCallback, ReactNode } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { useAircraft } from "../aircraft/AircraftQueries"
import { clearSelectedMissionIds } from "../redux/missions-table-slice"
import { useBulkAssignDrone } from "./PilotTrackingMissionQueries"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { pluralize } from "@nova/util-strings"

export const AssignDroneDropdown = ({
    missionIds,
    hasMixedDroneAssignment,
    defaultCheckedDroneId,
    trigger = <ButtonSecondary title="Assign Drone" RightIcon={Icons.ArrowDropdown} />,
    subtitle = "Apply a drone to the selected flights.",
    disabled,
    position,
}: {
    missionIds: string[]
    hasMixedDroneAssignment: boolean
    defaultCheckedDroneId?: string | null
    trigger?: ReactNode
    subtitle?: string
    disabled?: boolean
    position?: DropdownPosition
}) => {
    const dispatch = useAppDispatch()
    const [search, setSearch] = useState("")
    const [selectedDroneId, setSelectedDroneId] = useState<string | null>(
        defaultCheckedDroneId ?? null
    )
    const [warningDismissed, setWarningDismissed] = useState(false)

    const { aircraft } = useAircraft()
    const { assignDrone } = useBulkAssignDrone(missionIds)

    const filtered = useMemo(
        () =>
            aircraft.filter((a) =>
                (a.nickname ?? a.model)?.toLowerCase().includes(search.toLowerCase())
            ),
        [aircraft, search]
    )

    const resetState = useCallback(() => {
        setSelectedDroneId(null)
        setSearch("")
        setWarningDismissed(false)
    }, [])

    const handleApply = useCallback(async () => {
        if (!selectedDroneId) return
        try {
            await assignDrone(selectedDroneId)
            dispatch(clearSelectedMissionIds())
            dispatch(
                setGlobalNotificationContext({
                    message: `Drone assigned to ${pluralize({ count: missionIds.length, str: "flight" })}.`,
                    status: GlobalNotificationStatus.success,
                    timeoutInMs: 3000,
                })
            )
        } catch {
            dispatch(
                setGlobalNotificationContext({
                    message: "Failed to assign drone. Please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 5000,
                })
            )
        }
    }, [selectedDroneId, assignDrone, missionIds.length, dispatch])

    return (
        <Dropdown
            onClose={resetState}
            onOpenChange={() => setSelectedDroneId(defaultCheckedDroneId ?? null)}
            disabled={disabled}
        >
            <Dropdown.Trigger as="div">{trigger}</Dropdown.Trigger>
            <Dropdown.Menu
                unstyled
                className="w-[300px] rounded-xl bg-gray-700 shadow-dp4 overflow-hidden"
                position={position}
            >
                <div className="flex flex-col gap-1 px-4 pt-4 pb-3">
                    <span className="text-white font-medium">Assign Drone</span>
                    <span className="text-sm text-gray-400">{subtitle}</span>
                </div>
                {hasMixedDroneAssignment && !warningDismissed && (
                    <div className="px-4 pb-3">
                        <AlertContainer
                            type="warning"
                            title="Mixed Drone Assignments"
                            body={
                                <small className="text-xs text-gray-100">
                                    Some flights already have a drone assigned. Applying a new drone
                                    will overwrite it for all selected flights.
                                </small>
                            }
                            closeButtonOverride={
                                <Icons.LargeIcon
                                    Icon={Icons.Close}
                                    className="!m-0 text-gray-100 hover:text-white transition-colors cursor-pointer"
                                    iconClassName="!m-0"
                                    onClick={() => setWarningDismissed(true)}
                                />
                            }
                        />
                    </div>
                )}
                <div className="px-4 pb-3 text-white">
                    <InputOutlined
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        placeholder="Search for a drone"
                        leftItem={<Icons.Icon Icon={Icons.MagnifyingGlass} className="mr-1" />}
                        autoFocus
                    />
                </div>
                <ScrollableArea className="max-h-[300px]">
                    {filtered.map((a) => (
                        <Dropdown.Item
                            key={a.id}
                            clickBehaviour="keep-open"
                            onClick={() => setSelectedDroneId(a.id)}
                            className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-500 transition-colors cursor-pointer"
                        >
                            <div
                                className={classNames(
                                    "w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors",
                                    {
                                        "border-blue-400": selectedDroneId === a.id,
                                        "border-gray-400": selectedDroneId !== a.id,
                                    }
                                )}
                            >
                                {selectedDroneId === a.id && (
                                    <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                                )}
                            </div>
                            <span className="text-sm text-white">{a.nickname ?? a.model}</span>
                        </Dropdown.Item>
                    ))}
                </ScrollableArea>
                <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-600">
                    <Dropdown.Close as={ButtonTertiary} title="Cancel" />
                    <Dropdown.Close
                        as={ButtonPrimary}
                        title="Apply"
                        disabled={!selectedDroneId}
                        onClick={handleApply}
                        closeBehaviour="wait"
                    />
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}
